import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/components/auth/AuthContext';
import { authApi } from '@/api/auth';
import { toast } from 'sonner';
import {
  Check,
  X,
  Loader2,
  Zap,
  Users,
  Building2,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';

const tierIcons = {
  free: Zap,
  pro: Sparkles,
  team: Users,
  enterprise: Building2,
};

const tierColors = {
  free: 'border-gray-500',
  pro: 'border-blue-500 bg-blue-500/5',
  team: 'border-purple-500 bg-purple-500/5',
  enterprise: 'border-amber-500 bg-amber-500/5',
};

const PricingPage = ({ onClose }) => {
  const { isAuthenticated, tier: currentTier } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAnnual, setIsAnnual] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const data = await authApi.getPricingTiers();
        setTiers(data.tiers);
      } catch (error) {
        toast.error('Failed to load pricing');
      } finally {
        setLoading(false);
      }
    };
    fetchTiers();
  }, []);

  const handleSelectPlan = async (tierId) => {
    if (tierId === 'free') {
      toast.info('Free plan is always available');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Please sign in to upgrade');
      return;
    }

    if (tierId === currentTier) {
      toast.info('You are already on this plan');
      return;
    }

    setCheckoutLoading(tierId);
    try {
      const billingCycle = isAnnual ? 'annual' : 'monthly';
      const { checkout_url } = await authApi.createCheckout(tierId, billingCycle);
      window.location.href = checkout_url;
    } catch (error) {
      toast.error(error.message || 'Failed to create checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const formatLimit = (value) => {
    if (value === -1) return 'Unlimited';
    return value.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
        <p className="text-gray-400 text-lg mb-8">
          Start free and scale as you grow. Cancel anytime.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center space-x-4">
          <Label className={cn(!isAnnual && 'text-white')}>Monthly</Label>
          <Switch
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
          />
          <Label className={cn(isAnnual && 'text-white')}>
            Annual
            <Badge className="ml-2 bg-green-500/20 text-green-400 border-green-500/30">
              Save 30%
            </Badge>
          </Label>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((tier) => {
          const Icon = tierIcons[tier.id] || Zap;
          const isCurrentTier = tier.id === currentTier;
          const price = isAnnual ? tier.price_annual : tier.price_monthly;
          const monthlyEquivalent = isAnnual ? (tier.price_annual / 12).toFixed(2) : null;

          return (
            <div
              key={tier.id}
              className={cn(
                'relative rounded-xl border-2 p-6 transition-all',
                tierColors[tier.id],
                isCurrentTier && 'ring-2 ring-green-500 ring-offset-2 ring-offset-slate-900'
              )}
            >
              {isCurrentTier && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500">
                  Current Plan
                </Badge>
              )}

              {/* Tier Header */}
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold">{tier.name}</h3>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="text-4xl font-bold">
                  ${price === 0 ? '0' : (isAnnual ? monthlyEquivalent : price)}
                  <span className="text-base font-normal text-gray-400">/mo</span>
                </div>
                {isAnnual && price > 0 && (
                  <div className="text-sm text-gray-500 mt-1">
                    ${tier.price_annual}/year billed annually
                  </div>
                )}
              </div>

              {/* Limits */}
              <div className="space-y-3 mb-6 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Machines</span>
                  <span className="font-medium">{formatLimit(tier.limits.machines)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">States/Machine</span>
                  <span className="font-medium">{formatLimit(tier.limits.states_per_machine)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Exports</span>
                  <span className="font-medium">{formatLimit(tier.limits.exports)}{tier.limits.exports > 0 && tier.limits.exports !== -1 && '/mo'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Team Members</span>
                  <span className="font-medium">{formatLimit(tier.limits.users)}</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2 mb-6 text-sm">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                onClick={() => handleSelectPlan(tier.id)}
                disabled={checkoutLoading === tier.id || (isCurrentTier && tier.id !== 'free')}
                className={cn(
                  'w-full',
                  tier.id === 'pro' && 'bg-blue-600 hover:bg-blue-700',
                  tier.id === 'team' && 'bg-purple-600 hover:bg-purple-700',
                  tier.id === 'enterprise' && 'bg-amber-600 hover:bg-amber-700'
                )}
                variant={tier.id === 'free' ? 'outline' : 'default'}
              >
                {checkoutLoading === tier.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isCurrentTier ? (
                  'Current Plan'
                ) : tier.id === 'free' ? (
                  'Get Started'
                ) : (
                  `Upgrade to ${tier.name}`
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="text-center mt-12 text-sm text-gray-500">
        <p>All plans include SSL encryption and 99.9% uptime SLA.</p>
        <p className="mt-2">Questions? Contact us at support@statemachinefactory.com</p>
      </div>
    </div>
  );
};

export default PricingPage;
